import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import { SegmentItem } from "store/segments/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import Button from "components/buttons/Button";
import SearchInput from "components/inputs/SearchInput/SearchInput";
import { Modal } from "components/Modal/Modal";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import styles from "./AddToSegmentModal.module.scss";

const AddToSegmentModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isFetched, setFetched] = useState<boolean>(false);
  const [segmentsList, setSegmentsList] = useState<SegmentItem[]>([]);
  const [segmentsListFull, setSegmentsListFull] = useState<SegmentItem[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedSegments, setSelectedSegments] = useState<number[]>([]);
  const selectedItems = useAppSelector((store) => store.players.selectedItems);

  const handleLoadSegments = async () => {
    setFetched(false);
    try {
      const result = await instance.get(
        `${apiRoutes.segmentsList}?limit=1000&offset=0`,
      );
      setSegmentsListFull(result.data.items);
    } catch {
      console.log("handleLoadSegments error");
    }
    setFetched(true);
  };

  useEffect(() => {
    handleLoadSegments();
  }, []);

  useEffect(() => {
    setSegmentsList(
      segmentsListFull.filter((el) =>
        el.title?.toLowerCase().includes(searchValue?.toLowerCase()),
      ),
    );
  }, [segmentsListFull, searchValue]);

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
  };

  const handleApply = async () => {
    try {
      await instance.put(apiRoutes.addUsersToSegments, {
        userIds: selectedItems,
        segmentIds: selectedSegments,
      });
      dispatch(
        showNotification({
          title: t("success"),
          message: t("addUserSuccess"),
          type: NotificationTypes.success,
        }),
      );
      handleCloseClick();
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("addUserError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const onSearch = (e: string) => {
    setSearchValue(e);
  };

  const handleSegmentClick = (id: number) => {
    if (selectedSegments.includes(id)) {
      setSelectedSegments(selectedSegments.filter((el) => el !== id));
    } else {
      setSelectedSegments([...selectedSegments, id]);
    }
  };

  return (
    <>
      <Modal onClose={handleCloseClick}>
        <Modal.Header>
          <p>{t("playersAddToSegment")}</p>
        </Modal.Header>
        <Modal.Content className={styles.content}>
          {isFetched ? (
            <div className={styles.body}>
              <div className="games-order-search">
                <SearchInput
                  placeholder={t("searchSegment")}
                  value={searchValue}
                  onChange={onSearch}
                  isSmall
                />
              </div>

              <div
                className={`games-order-list segments-order-list ${styles.segmentsList}`}
              >
                <div className="item">
                  <div className="id">ID</div>
                  <div className="name">{t("name").toUpperCase()}</div>
                  <div className="type">{t("type").toUpperCase()}</div>
                  <div className="active">{t("active").toUpperCase()}</div>
                </div>

                {segmentsList.map((el) => (
                  <div
                    className={`item ${selectedSegments.includes(el.id ?? 0) ? "active" : ""}`}
                    key={el.id}
                    onClick={() => handleSegmentClick(el.id ?? 0)}
                  >
                    <div className="id">{el.id}</div>
                    <div className="name">{el.title}</div>
                    <div className="type">{t(el.type)}</div>
                    <div className="active">
                      <div
                        className={`active-label ${el.isActive ? "active" : ""}`}
                      >
                        {el.isActive ? "On" : "Off"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <ModalTabSkeleton />
          )}
        </Modal.Content>

        <Modal.Footer>
          <Button
            isSecondary
            txt={t("cancel")}
            handleClick={handleCloseClick}
          />
          <Button isPrimary handleClick={handleApply} txt={t("save")} />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddToSegmentModal;
